import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { prepareData } from 'helpers/prepare-req-data'
import { prepParams } from 'helpers/prep-req-url'
import { getRespData  } from 'helpers/get-resp-data'
import client from 'api'
import _ from 'lodash'

import { v4 as uuid4 } from 'uuid'


const initialState = {
    loadAAListStatus: 'idle',
    loadAAListError: undefined as any,
    loadAAListLastArgs: undefined as any,
    loadAAStatus: 'idle',
    loadAAError: undefined as any,
    saveAAStatus: 'idle',
    saveAAError: undefined as any,
    deleteAAStatus: 'idle',
    deleteAAError: undefined as any,

    aaList: [] as any[],
    formItem: undefined as any,
    filterValues: {} as any,
    filterLookupValues: {} as any,

    directLoad: false,
    load: false,
    activeDivRef: null as any,

    filterValuesId: null,
    valuesFromDB: false,
    filterVisibleId: null,
    filterVisible: true,
}


export const loadAAList = createAsyncThunk('aASlice/loadAAList', async ({ globalFilters, forceReload }: any, { rejectWithValue, getState }: any) => {
    const lastArgs = getState().aASlice.loadAAListLastArgs
    if (_.isEqual(lastArgs, { globalFilters }) && !forceReload) { return }

    let promises = []
    {
        const [url, params] = prepParams(`/api/ixweb/aa/`, globalFilters)
        promises.push(client.get(
            url,
            {
                params,
            }
        ))
    }

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: value1.map((x: any) => ({
            ...x,
        })),
        args: { globalFilters }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadAAListStatus } = getState().aASlice
        return loadAAListStatus !== 'pending'
    }
})

export const loadAA = createAsyncThunk('aASlice/loadAA', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.get(
        `/api/ixweb/aa/?action=eql&HDR_ID=${id}`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { loadAAStatus } = getState().aASlice
        return loadAAStatus !== 'pending'
    }
})

export const saveAA = createAsyncThunk('aASlice/saveAA', async ({ aa }: any, { rejectWithValue }: any) => {
    let promises = []
    if (aa?.HDR_ID) {
        promises.push(client.put(
            `/api/ixweb/aa/${ aa?.HDR_ID }/`,
            prepareData(aa),
        ))
    } else {
        promises.push(client.post(
            `/api/ixweb/aa/`,
            prepareData(aa),
        ))
    }

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue, true)

    return {
        data: {
            ...value1,
        }
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { saveAAStatus } = getState().aASlice
        return saveAAStatus !== 'pending'
    }
})

export const deleteAA = createAsyncThunk('aASlice/deleteAA', async ({ id }: any, { rejectWithValue }: any) => {
    let promises = []
    promises.push(client.delete(
        `/api/ixweb/aa/${id}/`,
    ))

    const [value1Res] = await Promise.all(promises)
    const value1 = getRespData(value1Res, rejectWithValue)

    return {
        data: id,
    }
}, {
    condition: ({}: any, { getState }: any) => {
        const { deleteAAStatus } = getState().aASlice
        return deleteAAStatus !== 'pending'
    }
})

const AaSlice = createSlice({
    name: 'aASlice',
    initialState,
    reducers: {
        resetloadAAListError: (state) => {
            state.loadAAListError = undefined
        },
        resetloadAA: (state) => {
            state.formItem = undefined
            state.loadAAError = undefined
        },
        resetloadAAError: (state) => {
            state.loadAAError = undefined
        },
        resetsaveAA: (state) => {
            state.formItem = undefined
            state.saveAAError = undefined
        },
        resetsaveAAError: (state) => {
            state.saveAAError = undefined
        },
        resetdeleteAAError: (state) => {
            state.deleteAAError = undefined
        },
        removeFromAaListById: (state, action) => {
            state.aaList = state.aaList.filter((x: any) => x.HDR_ID !== action.payload)
        },
        removeFromAaListByIds: (state, action) => {
            state.aaList = state.aaList.filter((x: any) => action.payload.indexOf(x.HDR_ID) === -1)
        },
        setFilterValues: (state, action) => {
            if (action.payload === undefined || action.payload === null) {
                state.filterValues = {}
            } else {
                state.filterValues = { ...state.filterValues, ...action.payload }
            }
        },
        setFilterLookupValue: (state, action) => {
            const { name, value, formValue } = action.payload
            if (name) {
                state.filterLookupValues[name] = value
                state.filterValues[name] = formValue
            }
        },
        setDirectLoad: (state, action) => { state.directLoad = action.payload },
        setLoad: (state, action) => { state.load = action.payload },
        setActiveDivRef: (state, action) => { state.activeDivRef = action.payload},
        setFilterValuesId: (state, action) => {
            state.filterValuesId = action.payload
        },
        setValuesFromDB: (state, action) => {
            state.valuesFromDB = action.payload
        },
        setFilterVisibleId: (state, action) => {
            state.filterVisibleId = action.payload
        },
        setFilterVisible: (state, action) => {
            state.filterVisible = action.payload
        },
        preloadData: (state, action) => {
            state.aaList = action.payload.map((x: any) => ({ ...x, HDR_ID: uuid4()}))
            state.loadAAListStatus = 'preloaded'
            state.loadAAListLastArgs = ''
        },
        clearPreloadedData: (state) => {
            state.aaList = []
            state.loadAAListStatus = 'idle'
            state.loadAAListLastArgs = undefined
        },
    },
    extraReducers: builder => {
        builder.addCase(loadAAList.pending, (state) => {
            state.loadAAListStatus = 'pending'
            state.loadAAListError = undefined
        })
        builder.addCase(loadAAList.fulfilled, (state, action) => {
            state.loadAAListStatus = 'idle'

            if (!action.payload) { return }

            state.loadAAListLastArgs = action.payload.args

            {
                state.aaList = action.payload.data

            }
        })
        builder.addCase(loadAAList.rejected, (state, action) => {
            state.loadAAListStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadAAListError = action.error.message
            } else {
                state.loadAAListError = action.payload
            }
        })

        builder.addCase(loadAA.pending, (state) => {
            state.loadAAStatus = 'pending'
            state.loadAAError = undefined
        })
        builder.addCase(loadAA.fulfilled, (state, action) => {
            state.loadAAStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.aaList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.aaList.push(newItem) }
                else { state.aaList[i] = { ...state.aaList[i], ...newItem } }
            }
        })
        builder.addCase(loadAA.rejected, (state, action) => {
            state.loadAAStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.loadAAError = action.error.message
            } else {
                state.loadAAError = action.payload
            }
        })

        builder.addCase(saveAA.pending, (state) => {
            state.saveAAStatus = 'pending'
            state.saveAAError = undefined
        })
        builder.addCase(saveAA.fulfilled, (state, action) => {
            state.saveAAStatus = 'idle'

            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                state.formItem = newItem
            }
            {
                const newItem = Array.isArray(action.payload.data) ? action.payload.data[0] : action.payload.data
                const i = state.aaList.findIndex((x: any) => x.HDR_ID === newItem.HDR_ID)
                if (i === -1) { state.aaList.push(newItem) }
                else { state.aaList[i] = { ...state.aaList[i], ...newItem } }
            }
        })
        builder.addCase(saveAA.rejected, (state, action) => {
            state.saveAAStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.saveAAError = action.error.message
            } else {
                state.saveAAError = action.payload
            }
        })

        builder.addCase(deleteAA.pending, (state) => {
            state.deleteAAStatus = 'pending'
            state.deleteAAError = undefined
        })
        builder.addCase(deleteAA.fulfilled, (state, action) => {
            state.deleteAAStatus = 'idle'

            {
                if (Array.isArray(action.payload.data)) {
                    state.aaList = state.aaList.filter((x: any) => !action.payload.data.includes(x.HDR_ID))
                } else {
                    state.aaList = state.aaList.filter((x: any) => x.HDR_ID !== action.payload.data)
                }
            }
        })
        builder.addCase(deleteAA.rejected, (state, action) => {
            state.deleteAAStatus = 'error'
            if (action.error.message !== 'Rejected') {
                state.deleteAAError = action.error.message
            } else {
                state.deleteAAError = action.payload
            }
        })

    }
})

export const {
    resetloadAAListError,
    resetloadAA,
    resetloadAAError,
    resetsaveAA,
    resetsaveAAError,
    resetdeleteAAError,
    removeFromAaListById,
    removeFromAaListByIds,
    setFilterValues,
    setFilterLookupValue,
    setDirectLoad,
    setLoad,
    setActiveDivRef,
    setFilterValuesId,
    setValuesFromDB,
    setFilterVisible,
    setFilterVisibleId,
    preloadData,
    clearPreloadedData,
} = AaSlice.actions
export default AaSlice.reducer
